import TicketCuticle from '@/components/manage/TicketCuticle';
import PriceDisplay from '@/components/PriceDisplay';
import { manageBookingSearchLoadingSelector } from '@/features/loading/loadingSelectors';
import { profileDataSelector } from '@/features/profile/profileSelectors';
import { useBookingSearch } from '@/hooks/useBookingSearch';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import type { BookingSearchDTO } from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { Button, Icons, Typography } from '@packages/webshop-design-system';
import { useEffect, useRef, useState, type FC, type ReactNode } from 'react';
import { useMediaQuery } from 'react-responsive';

interface BookingItemLayoutProps {
  bookingSearchItem: BookingSearchDTO;
  isActive: boolean;
  children: ReactNode;
  dateRange?: string;
  isExpired?: boolean;
}

const BookingItemLayout: FC<BookingItemLayoutProps> = ({
  bookingSearchItem,
  isActive,
  children,
  dateRange,
  isExpired,
}) => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const profile = useSelector(profileDataSelector);
  const loading = useSelector(manageBookingSearchLoadingSelector);
  const [wasClicked, setWasClicked] = useState(false);
  const prevLoadingRef = useRef(loading);

  useEffect(() => {
    if (prevLoadingRef.current && !loading) {
      setWasClicked(false);
    }
    prevLoadingRef.current = loading;
  }, [loading]);

  const isRefunded = Boolean(
    bookingSearchItem.status &&
      ['REFUNDED', 'RELEASED', 'CANCELLED'].includes(bookingSearchItem.status)
  );

  const onBookingSearch = useBookingSearch();

  const renderHeaderTag = () => {
    if (!isRefunded && isActive && !isExpired) {
      return null;
    }

    const Icon = isRefunded ? Icons.ticketReturn : Icons.history;

    return (
      <div className="absolute -bottom-[11px] right-0.5 flex gap-1 rounded-full border border-neutral bg-white px-2 py-1 laptop:right-1.5">
        <Icon className="h-3 w-3 text-neutral" />
        <Typography variant="label" className="text-neutral">
          <TransText i18nKey={isRefunded ? 'refunded' : 'pastBooking'} />
        </Typography>
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      <div
        className={cn(
          'relative flex justify-between rounded-t-lg border-x border-b border-t border-primary bg-primary-pale p-3 laptop:p-4',
          { 'border-neutral-light': isRefunded }
        )}
      >
        <div className="flex flex-col gap-0.5 laptop:flex-row laptop:items-center laptop:gap-2">
          <Typography
            variant={isLaptopOrBigger ? 'subtitle' : 'body1-bold'}
            className={cn('text-dark', {
              'text-neutral': isRefunded,
            })}
          >
            <>
              <TransText i18nKey="booking" /> {bookingSearchItem.code}
            </>
          </Typography>
          {dateRange && (
            <Typography
              variant={isLaptopOrBigger ? 'body1' : 'body2'}
              className="text-neutral"
            >
              {dateRange}
            </Typography>
          )}
        </div>
        {!isRefunded && (
          <Typography
            variant={isLaptopOrBigger ? 'subtitle' : 'body1-bold'}
            className="text-primary"
          >
            <PriceDisplay amount={bookingSearchItem.totalPrice?.amount} />
          </Typography>
        )}
        {renderHeaderTag()}
      </div>
      <div
        className={cn(
          'flex flex-col gap-4 border-x border-neutral-light bg-white p-3 pb-6 text-dark laptop:flex-row laptop:p-4',
          { 'bg-primary-pale': isRefunded }
        )}
      >
        {children}
      </div>
      <div className="bg-white">
        <TicketCuticle isVertical={false} />
      </div>
      <div
        className={cn(
          'flex flex-col gap-2 rounded-b-lg border-x border-b border-neutral-light bg-white p-3 pt-6 laptop:grid laptop:grid-cols-3 laptop:p-4',
          { 'bg-primary-pale': isRefunded }
        )}
      >
        <Button
          data-testid="profile-booking-view-details"
          size="medium"
          variant={isRefunded ? 'tertiary' : 'secondary'}
          className="laptop:col-start-3"
          onClick={() => {
            setWasClicked(true);
            onBookingSearch({
              email: profile?.email,
              bookingNumber: bookingSearchItem.code,
            });
          }}
          isPending={loading && wasClicked}
          isDisabled={loading && !wasClicked}
          endAdornment={<Icons.arrowRight width={20} height={20} />}
        >
          <TransText i18nKey="viewDetails" />
        </Button>
      </div>
    </div>
  );
};

export default BookingItemLayout;
